/* Exit / décès dialogs for the Suivi dashboard.
 *
 * Three separate decisions, deliberately not merged into one « supprimer »:
 *  - MemberActionsModal — the chooser, so a member row keeps one affordance
 *  - ExitModal          — one participant, or the whole dossier (same form, different
 *                         blast radius, and the copy says which)
 *  - DeathModal         — an outcome, NOT a withdrawal: the rest of the dossier keeps
 *                         its follow-up, so it never runs through the exit form
 *
 * Exiting keeps everything the participant already answered. The one destructive choice
 * is « ne plus utiliser mes données », which hides them from the research data for good —
 * it is asked as an explicit second question, never defaulted.
 */
import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { IonModal } from "@ionic/react";
import {
  ModalCard,
  ModalTitle,
  ModalDescription,
  ModalActions,
  ModalPrimaryButton,
  ModalQuietButton,
  ModalError,
  ModalFields,
} from "../../components/atoms/ModalKit";
import { TextField } from "../../components/atoms/LoginTextField";
import { ExitReasonId, SuiviMemberDetail } from "../../services/suivi.service";

// Order mirrors ref_exit_reasons. DEATH is absent on purpose: a death is recorded
// through its own action so it never gets filed as a withdrawal.
const EXIT_REASONS: { id: ExitReasonId; key: string }[] = [
  { id: ExitReasonId.WITHDRAWAL, key: "suivi_exit_reason_withdrawal" },
  { id: ExitReasonId.SCREEN_FAILURE, key: "suivi_exit_reason_screen_failure" },
  { id: ExitReasonId.LOST_TO_FOLLOW_UP, key: "suivi_exit_reason_lost" },
  { id: ExitReasonId.ENROLLMENT_ERROR, key: "suivi_exit_reason_error" },
  { id: ExitReasonId.OTHER, key: "suivi_exit_reason_other" },
];

interface MemberActionsModalProps {
  isOpen: boolean;
  member: SuiviMemberDetail | null;
  memberName: string;
  onDismiss: () => void;
  onRemove: () => void;
  onExit: () => void;
  onRecordDeath: () => void;
  onClearDeath: () => void;
  onSendAccessLink: () => void;
}

export const MemberActionsModal = ({
  isOpen,
  member,
  memberName,
  onDismiss,
  onRemove,
  onExit,
  onRecordDeath,
  onClearDeath,
  onSendAccessLink,
}: MemberActionsModalProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <IonModal isOpen={isOpen} className="resonance-modal" backdropDismiss onWillDismiss={onDismiss}>
      <ModalCard>
        <ModalTitle>{memberName}</ModalTitle>
        <ActionList>
          {/* Nothing to send once they have registered, and never to someone who has
              left the study. */}
          {member?.invitable && !member?.registered && !member?.exited && (
            <ActionRow type="button" onClick={onSendAccessLink}>
              {member.invited_at
                ? t("suivi_access_link_resend_title")
                : t("suivi_access_link_title")}
            </ActionRow>
          )}
          {/* Only while the member has no data at all — a composition fix, not an exit. */}
          {member?.deletable && (
            <ActionRow type="button" onClick={onRemove}>
              {t("suivi_remove_member_title")}
            </ActionRow>
          )}
          {!member?.exited && (
            <ActionRow type="button" onClick={onExit}>
              {t("suivi_exit_member_title")}
            </ActionRow>
          )}
          {member?.deceased ? (
            <ActionRow type="button" onClick={onClearDeath}>
              {t("suivi_death_clear_title")}
            </ActionRow>
          ) : (
            <ActionRow type="button" onClick={onRecordDeath}>
              {t("suivi_death_title")}
            </ActionRow>
          )}
        </ActionList>
        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>{t("common_cancel")}</ModalQuietButton>
        </ModalActions>
      </ModalCard>
    </IonModal>
  );
};

interface ExitModalProps {
  isOpen: boolean;
  /** Dossier-wide withdrawal rather than a single participant — changes the copy only. */
  isDossier: boolean;
  targetName: string;
  saving: boolean;
  error: string | null;
  onDismiss: () => void;
  onConfirm: (payload: {
    ref_exit_reason_id: number;
    exit_note: string | null;
    keep_data: boolean;
  }) => void;
}

export const ExitModal = ({
  isOpen,
  isDossier,
  targetName,
  saving,
  error,
  onDismiss,
  onConfirm,
}: ExitModalProps): ReactElement => {
  const { t } = useTranslation();
  const [reasonId, setReasonId] = useState<ExitReasonId>(ExitReasonId.WITHDRAWAL);
  const [note, setNote] = useState("");
  const [keepData, setKeepData] = useState(true);

  return (
    <IonModal isOpen={isOpen} className="resonance-modal" backdropDismiss onWillDismiss={onDismiss}>
      <ModalCard>
        <ModalTitle>
          {isDossier ? t("suivi_exit_dossier_title") : t("suivi_exit_member_title")}
        </ModalTitle>
        <ModalDescription>
          {isDossier
            ? t("suivi_exit_dossier_text", { name: targetName })
            : t("suivi_exit_member_text", { name: targetName })}
        </ModalDescription>

        <FieldLabel>{t("suivi_exit_reason_label")}</FieldLabel>
        <Choices>
          {EXIT_REASONS.map((reason) => (
            <Choice
              key={reason.id}
              type="button"
              $selected={reasonId === reason.id}
              aria-pressed={reasonId === reason.id}
              onClick={() => setReasonId(reason.id)}
            >
              {t(reason.key)}
            </Choice>
          ))}
        </Choices>

        <ModalFields>
          <TextField
            type="text"
            onChange={(text) => setNote(text)}
            value={note}
            placeholder={t("suivi_exit_note_label")}
            label={t("suivi_exit_note_label")}
          />
        </ModalFields>

        {/* The data decision, asked separately because it is the irreversible half. */}
        <FieldLabel>{t("suivi_exit_data_label")}</FieldLabel>
        <Choices>
          <Choice type="button" $selected={keepData} aria-pressed={keepData} onClick={() => setKeepData(true)}>
            {t("suivi_exit_data_keep")}
          </Choice>
          <Choice type="button" $selected={!keepData} aria-pressed={!keepData} onClick={() => setKeepData(false)}>
            {t("suivi_exit_data_drop")}
          </Choice>
        </Choices>
        {!keepData && <WarnText>{t("suivi_exit_data_drop_warning")}</WarnText>}

        {error && <ModalError>{error}</ModalError>}

        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>{t("common_cancel")}</ModalQuietButton>
          <ModalPrimaryButton
            disabled={saving}
            onClick={() =>
              onConfirm({
                ref_exit_reason_id: reasonId,
                exit_note: note.trim() ? note.trim() : null,
                keep_data: keepData,
              })
            }
          >
            {t("suivi_exit_confirm")}
          </ModalPrimaryButton>
        </ModalActions>
      </ModalCard>
    </IonModal>
  );
};

interface DeathModalProps {
  isOpen: boolean;
  targetName: string;
  saving: boolean;
  error: string | null;
  onDismiss: () => void;
  onConfirm: (payload: { death_date: string | null; note: string | null }) => void;
}

export const DeathModal = ({
  isOpen,
  targetName,
  saving,
  error,
  onDismiss,
  onConfirm,
}: DeathModalProps): ReactElement => {
  const { t } = useTranslation();
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");

  return (
    <IonModal isOpen={isOpen} className="resonance-modal" backdropDismiss onWillDismiss={onDismiss}>
      <ModalCard>
        <ModalTitle>{t("suivi_death_title")}</ModalTitle>
        <ModalDescription>{t("suivi_death_text", { name: targetName })}</ModalDescription>

        <ModalFields>
          {/* Both optional: a death is often known before its exact date is. */}
          <TextField
            type="date"
            onChange={(text) => setDate(text)}
            value={date}
            placeholder={t("suivi_death_date_label")}
            label={t("suivi_death_date_label")}
          />
          <TextField
            type="text"
            onChange={(text) => setNote(text)}
            value={note}
            placeholder={t("suivi_exit_note_label")}
            label={t("suivi_exit_note_label")}
          />
        </ModalFields>

        {error && <ModalError>{error}</ModalError>}

        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>{t("common_cancel")}</ModalQuietButton>
          <ModalPrimaryButton
            disabled={saving}
            onClick={() =>
              onConfirm({
                death_date: date ? date : null,
                note: note.trim() ? note.trim() : null,
              })
            }
          >
            {t("suivi_death_confirm")}
          </ModalPrimaryButton>
        </ModalActions>
      </ModalCard>
    </IonModal>
  );
};

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 4px 0 8px;
`;

const ActionRow = styled.button`
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(var(--app-color-text-rgb, 15, 23, 42), 0.12);
  background: transparent;
  font-size: 15px;
  color: var(--app-color-text, #0f172a);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.6;
  }
`;

const FieldLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);
  margin: 12px 0 6px;
`;

const Choices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Choice = styled.button<{ $selected: boolean }>`
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 14px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? "var(--app-color-primary, #0f766e)"
        : "rgba(var(--app-color-text-rgb, 15, 23, 42), 0.15)"};
  background: ${({ $selected }) =>
    $selected ? "rgba(var(--app-color-primary-rgb, 15, 118, 110), 0.08)" : "transparent"};
  color: var(--app-color-text, #0f172a);
`;

const WarnText = styled.div`
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--ion-color-danger, #c0392b);
`;
